import React, { useCallback } from "react";
import { useStore, useSelector } from "react-redux";
import * as turf from "@turf/turf";
import { useRequest } from "ahooks";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import {
  ScreenSpaceEventType,
  defined,
  Cartographic,
  Math as CesiumMath,
} from "cesium";
import { ScreenSpaceEvent, ScreenSpaceEventHandler, useCesium } from "resium";

function PickEventHandler() {
  const { t } = useTranslation();
  const { select, dispatch } = useStore();
  const time = useSelector(select.layer.selectTime);
  const { data, error } = useRequest(
    `/geojson/buffer/v3_irregular_NO_ICE_passage_buffer_${time}.geojson`,
    { refreshDeps: [time] }
  );
  const { viewer } = useCesium();
  const drawing = useSelector(select.layer.selectDrawing);
  const polygonDrawing = useSelector(select.layer.selectPolygonDrawing);
  const mouseOverHandler = useCallback(
    (e) => {
      const element = document.getElementById("main-container");
      const { scene } = viewer;
      const { endPosition } = e;
      if (drawing || polygonDrawing) {
        element.style.cursor = "crosshair";
      } else {
        const picked = scene.pick(endPosition);
        if (defined(picked)) {
          element.style.cursor = "pointer";
        } else {
          element.style.cursor = "default";
        }
      }
    },
    [viewer, drawing, polygonDrawing]
  );
  const clickHandler = useCallback(
    (e) => {
      const { scene, camera } = viewer;
      const { position } = e;
      const cartesian = camera.pickEllipsoid(position, scene.globe.ellipsoid);
      if (!cartesian) return;
      const cartographic = Cartographic.fromCartesian(cartesian);
      const lng = CesiumMath.toDegrees(cartographic.longitude);
      const lat = CesiumMath.toDegrees(cartographic.latitude);
      if (polygonDrawing) {
        dispatch.layer.addVertex([lng, lat]);
        // dispatch.layer.updateFields({ drawing: undefined });
      } else if (drawing) {
        if (error) {
          message.warning(t("outOfDate"), 10);
          return;
        }
        let pt = turf.point([lng, lat]);
        if (lng < 0) pt = turf.point([lng + 360, lat]); //lng from 0 to 360
        const isContained = data.features.some((feature) => {
          // const poly = turf.polygon(feature.geometry.coordinates);
          return turf.inside(pt, feature);
        });
        if (isContained) {
          if (drawing === "start") {
            dispatch.layer.updateStartPoint([lng, lat]);
          }
          if (drawing === "end") {
            dispatch.layer.updateEndPoint([lng, lat]);
          }
        } else {
          message.warning(t("outOfBoundary"), 10);
        }
        dispatch.layer.updateFields({ drawing: undefined });
      } else {
        const picked = scene.pick(position);
        if (defined(picked)) {
          const { id } = picked;
          if (!id) return;
          const model = id.properties.model.getValue();
          if (model === "port") {
            const name = id.properties.name.getValue();
            const enName = id.properties.enName.getValue();
            dispatch.panel.loadPort({ name, enName });
          } else if (model === "passage") {
            dispatch.panel.updateWeather({
              lng: Math.floor(lng).toFixed(1),
              lat: Math.floor(lat).toFixed(1),
            });
          }
        }
      }
    },
    [viewer, dispatch, drawing, polygonDrawing, error, t, data?.features]
  );

  return (
    <ScreenSpaceEventHandler>
      <ScreenSpaceEvent
        action={mouseOverHandler}
        type={ScreenSpaceEventType.MOUSE_MOVE}
      />
      <ScreenSpaceEvent
        action={clickHandler}
        type={ScreenSpaceEventType.LEFT_CLICK}
      />
    </ScreenSpaceEventHandler>
  );
}

export default PickEventHandler;
